// Achievement toast (spec §11.5.10).
//
// Subscribes to the user's gamification summary; when a new entry appears
// in achievements[], slides a toast in from the top-right with a
// brief gold/indigo particle burst. Auto-dismisses after 5s. Multiple
// concurrent unlocks queue and play sequentially.
//
// Intrinsic learners get nothing — the toast is suppressed entirely so
// the over-justification effect doesn't bleed into their experience.

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Fingerprint, ChevronsDown, Target, Library, Flame, Award, Crown,
  Mountain, Compass, Mic, Network, AlertTriangle, Sparkles, X,
} from 'lucide-react';
import { useMotivationalDriverVisibility } from '@shared/hooks/useMotivationalDriverVisibility';
import {
  subscribeGamificationSummary,
  ensureGamificationDoc,
} from '@shared/services/gamification';
import { lookupAchievement } from '@shared/constants/points-config';
import type { AchievementRecord, UserGamification } from '@shared/types/gamification';

const ICONS: Record<string, typeof Sparkles> = {
  first_print: Fingerprint,
  deep_diver: ChevronsDown,
  test_pilot: Target,
  polyglot: Library,
  week_warrior: Flame,
  month_master: Award,
  perfect_run: Crown,
  path_pioneer: Mountain,
  self_aware: Compass,
  voice_learner: Mic,
  knowledge_mapper: Network,
  contradiction_finder: AlertTriangle,
};

const TOAST_DURATION_MS = 5000;

interface AchievementToastProps {
  userId: string;
}

export function AchievementToast({ userId }: AchievementToastProps) {
  const visibility = useMotivationalDriverVisibility(userId);
  const [queue, setQueue] = useState<AchievementRecord[]>([]);
  const seenIds = useRef<Set<string> | null>(null);

  useEffect(() => {
    if (visibility.intrinsic) return;
    let active = true;
    void ensureGamificationDoc(userId);

    const unsub = subscribeGamificationSummary(userId, (summary) => {
      if (!active || !summary) return;
      const currentIds = new Set(summary.achievements.map((a) => a.id));
      // First snapshot: prime the seen set, don't toast pre-existing achievements.
      if (seenIds.current === null) {
        seenIds.current = currentIds;
        return;
      }
      const newRecords = (summary as UserGamification).achievements.filter(
        (a) => !seenIds.current!.has(a.id),
      );
      if (newRecords.length > 0) {
        setQueue((q) => [...q, ...newRecords]);
        for (const r of newRecords) seenIds.current!.add(r.id);
      }
    });

    return () => {
      active = false;
      unsub();
    };
  }, [userId, visibility.intrinsic]);

  if (visibility.intrinsic) return null;
  const current = queue[0] ?? null;

  return (
    <div className="fixed top-28 right-4 z-[60] pointer-events-none">
      <AnimatePresence>
        {current && (
          <ToastCard
            key={current.id + current.earnedAt}
            record={current}
            onDismiss={() => setQueue((q) => q.slice(1))}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

interface ToastCardProps {
  record: AchievementRecord;
  onDismiss: () => void;
}

function ToastCard({ record, onDismiss }: ToastCardProps) {
  const def = lookupAchievement(record.id);
  const Icon = ICONS[record.id] ?? Sparkles;
  const tone = def?.tone ?? 'gold';
  const accent = tone === 'gold' ? '#F0A500' : '#6C5CE7';
  const accentSoft = tone === 'gold' ? 'rgba(240, 165, 0, 0.18)' : 'rgba(108, 92, 231, 0.18)';

  useEffect(() => {
    const t = setTimeout(onDismiss, TOAST_DURATION_MS);
    return () => clearTimeout(t);
  }, [onDismiss]);

  return (
    <motion.div
      initial={{ opacity: 0, x: 60, scale: 0.96 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 60, scale: 0.96 }}
      transition={{ type: 'spring', stiffness: 240, damping: 24 }}
      className="pointer-events-auto relative w-80 rounded-xl border bg-dark-surface shadow-modal overflow-hidden"
      style={{ borderColor: `${accent}55` }}
      role="status"
      aria-live="polite"
    >
      <ParticleBurst tone={tone} />
      <Link
        to="/profile/progress"
        className="flex items-start gap-3 p-4 hover:bg-dark-hover transition-colors"
      >
        <div
          className="shrink-0 h-12 w-12 rounded-lg flex items-center justify-center"
          style={{ background: accentSoft, border: `1px solid ${accent}55` }}
        >
          <Icon size={22} style={{ color: accent }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-body uppercase tracking-wider text-content-muted">
            Achievement unlocked
          </div>
          <div className="font-heading text-base font-bold text-content truncate">
            {def?.title ?? record.id}
          </div>
          {def?.description && (
            <div className="text-xs font-body text-content-secondary mt-0.5 line-clamp-2">
              {def.description}
            </div>
          )}
          {record.xpAwarded > 0 && (
            <div className="mt-2 inline-flex items-center gap-1 text-xs font-body text-content">
              <Sparkles size={12} className="text-gold-500" />
              <span style={{ color: accent }}>+{record.xpAwarded} XP</span>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            onDismiss();
          }}
          aria-label="Dismiss"
          className="shrink-0 text-content-muted hover:text-content cursor-pointer"
        >
          <X size={16} />
        </button>
      </Link>
    </motion.div>
  );
}

// Light particle burst — radial gold + indigo dots, ~2s. No external dep.
function ParticleBurst({ tone }: { tone: 'gold' | 'indigo' }) {
  const primary = tone === 'gold' ? '#F0A500' : '#6C5CE7';
  const secondary = tone === 'gold' ? '#6C5CE7' : '#F0A500';
  const particles = Array.from({ length: 14 }).map((_, i) => {
    const angle = (i / 14) * Math.PI * 2;
    const dist = 70 + Math.random() * 30;
    return {
      id: i,
      dx: Math.cos(angle) * dist,
      dy: Math.sin(angle) * dist,
      color: i % 2 === 0 ? primary : secondary,
      size: 4 + Math.random() * 3,
      delay: Math.random() * 0.1,
    };
  });
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      <div className="absolute left-10 top-10">
        {particles.map((p) => (
          <motion.span
            key={p.id}
            initial={{ x: 0, y: 0, opacity: 1, scale: 0.6 }}
            animate={{ x: p.dx, y: p.dy, opacity: 0, scale: 1 }}
            transition={{ duration: 1.2, delay: p.delay, ease: 'easeOut' }}
            className="absolute rounded-full"
            style={{
              width: p.size,
              height: p.size,
              background: p.color,
              boxShadow: `0 0 6px ${p.color}`,
            }}
          />
        ))}
      </div>
    </div>
  );
}
